import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getProcessHistory, getProcessHistoryRecord, ProcessHistoryElement, ProcessHistoryRestore, ProcessHistoryRow, MaterialRef } from '../../api/assets';
import { ApiError } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import { BeforeAfterImages, LinkedMaterial } from './parts';
import { ELEMENT_COLS, EVAL_OPTION_LABEL, EvaluateBadge, QCBadge, RestoredBadge, fmtOccurred, restoreText } from './processHistoryLabels';
import { useMModeText } from '../../utils/mmodeText';
import { useParamValueText } from '../../utils/paramValueText';
import { useSchemaFieldNames } from '../../utils/useSchemaFieldNames';

// 历史加工数据详情: 加工前/后对比图 · 反馈 · 溯源 · 元素参数表 · processingData / params 精选.
//
// 2026-09-20 改版: 左栏由单张结果图改成与「加工数据中心」同款的前后对比 (复用 parts.BeforeAfterImages) ——
// "加工前"来自耗材还原 (视觉模型把加工痕迹从结果图里抹掉, 反推耗材原样), 不是实拍,
// 所以挂 AI 还原·推测 角标, 并在还原信息卡里摆出模型/表面判定/框占比/漂移/质检, 让人能自己判断可信度。

function parseJSON<T>(s: string | undefined, fallback: T): T {
    if (!s) return fallback;
    try { return JSON.parse(s) as T; } catch { return fallback; }
}

// processingData / params 的键名兜底表: 这些是 xcs2.7 上报字段, 尚未进 xtool-schema 字段字典,
// 所以字典里查不到显示名. 已进字典的键 (pathPlanning/fillPlanning/scanDirection/thickness/... )
// 走 schema_field 的 fieldName, 不看这张表; 表里剩的键待 SSOT 补录后逐条删除.
const PD_LABEL_FALLBACK: Record<string, string> = {
    mode: '加工模式', material: '材料 id', fanGear: '风扇档', purifierGear: '净化器档',
    purifierV3Gear: '净化器 V3', precautionCodes: '注意事项',
    isDangerousMaterial: '危险材料', isFlammability: '易燃',
    processMode: '加工模式', size: '幅面', accel: '加速度', maxPower: '最大功率', version: '版本',
    imageComposite: '图像合成', imageCompositeCoefficient: '合成系数', imagePlanning: '图像规划', overScan: '过扫',
};

function KVGrid({ obj, keyText, valueText }: {
    obj: Record<string, unknown>;
    keyText: (k: string) => string;
    valueText: (k: string, v: unknown) => string;
}) {
    const entries = Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (entries.length === 0) return <div className="text-xs text-slate-400">—</div>;
    return (
        <div className="grid gap-x-4 gap-y-1.5 text-xs" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
            {entries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-slate-50 py-1">
                    <span className="text-slate-400 truncate" title={k}>{keyText(k)}</span>
                    <span className="text-slate-700 font-mono text-right break-all">{valueText(k, v)}</span>
                </div>
            ))}
        </div>
    );
}

/**
 * recordId: 并表后的统一记录 id (≥ HISTORY_RECORD_ID_BASE, 2026-10-20) —— 从「材料加工」点进历史行时
 * 走新接口 /v1/assets/process-records/:id (同形), 返回链接也回到材料加工; 不传则按路由里的老 id 读老接口.
 */
function ProcessHistoryDetail({ recordId }: { recordId?: number }) {
    const modeText = useMModeText();
    // 元素参数列与 processingData/params 的枚举值统一走公共取值器 (各字段自己的字典).
    const paramText = useParamValueText();

    // processingData / params 的键名走 Schema 字段字典, 值走该字段挂的枚举来源字典 (enums_source),
    // 加工模式走 mm_mode —— 页面不维护任何 code→中文 表.
    const fieldNames = useSchemaFieldNames();
    const kvKeyText = (k: string) => fieldNames.get(k) || PD_LABEL_FALLBACK[k] || k;
    const kvValueText = (k: string, v: unknown): string => {
        if (typeof v === 'boolean') return v ? '是' : '否';
        if (typeof v === 'object') return JSON.stringify(v);
        return paramText(k, v);
    };

    const { id } = useParams<{ id: string }>();
    const [data, setData] = useState<{
        record: ProcessHistoryRow; elements: ProcessHistoryElement[]; material: MaterialRef | null;
        restore: ProcessHistoryRestore | null;
    } | null>(null);
    const [error, setError] = useState('');
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        const req = recordId ? getProcessHistoryRecord(recordId) : getProcessHistory(id);
        req.then((r) => { if (alive) setData(r); }).catch((e) => { if (alive) setError(e instanceof ApiError ? e.message : String(e)); });
        return () => { alive = false; };
    }, [id, recordId]);

    const r = data?.record;
    const rest = data?.restore || null;
    const opts = useMemo(() => parseJSON<string[]>(r?.evaluateOptions, []), [r]);
    const laser = useMemo(() => parseJSON<number[]>(r?.laserValues, []), [r]);
    const pd = useMemo(() => parseJSON<Record<string, unknown>>(r?.processingData, {}), [r]);
    const params = useMemo(() => parseJSON<Record<string, unknown>>(r?.params, {}), [r]);
    const extra = useMemo(() => parseJSON<Record<string, unknown>>(r?.extra, {}), [r]);

    if (error) return <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!r) return <div className="text-sm text-slate-400 p-6">加载中…</div>;

    return (
        <div className="space-y-4">
            <div>
                {recordId ? (
                    <Link to="/assets/process-records?tab=table&origin=history" className="text-xs text-blue-600 hover:underline">← 返回 材料加工</Link>
                ) : (
                    <Link to="/assets/process-history?tab=table" className="text-xs text-blue-600 hover:underline">← 返回 历史加工数据</Link>
                )}
                <div className="flex items-start justify-between gap-4 mt-1">
                    <div>
                        <div className="text-xl font-extrabold text-slate-900 tracking-tight">{r.materialName || '未命名材料'}</div>
                        <div className="text-xs text-slate-500 mt-1">
                            {r.deviceName || r.deviceIdRaw} · {modeText(r.mode)} · {r.elementCount} 个元素 · 上报于 {fmtOccurred(r.occurredAt)}
                            <span className="text-slate-400"> · 记录 #{r.id}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0"><EvaluateBadge type={r.evaluateType} /></div>
                </div>
            </div>

            <div className="grid grid-cols-[380px_1fr] gap-4 items-start">
                {/* 左: 结果图 + 反馈 + 溯源 */}
                <div className="space-y-3">
                    <div className="card p-3">
                        <BeforeAfterImages
                            before={rest?.restoredImageUrl} after={r.imageUrl} size={300} onZoom={setZoomSrc}
                            beforeBadge={<span className="px-1 py-0.5 rounded bg-violet-50 text-violet-600 text-[10px]">AI 还原 · 推测</span>}
                            beforeMissingText="这条还没做耗材还原"
                            afterMissingText={r.hasImage ? '结果图尚未上传到本平台存储' : '这次上报没有结果图'} />
                        <div className="text-[11px] text-slate-400 mt-2">
                            源数据只有加工<b>后</b>的结果图。「加工前」是模型从结果图反推出的耗材原样，
                            <b>不是实拍</b>，仅供比对参考。
                        </div>
                    </div>

                    {rest && (
                        <div className="card p-4 text-xs text-slate-600 space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="text-xs font-semibold text-slate-500">耗材还原</div>
                                <RestoredBadge />
                                <span className="ml-auto"><QCBadge verdict={rest.qcVerdict} text={restoreText('qc_verdict', rest.qcVerdict)} /></span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                                <div className="flex justify-between gap-2 border-b border-slate-50 py-1">
                                    <span className="text-slate-400">表面判定</span>
                                    <span className="text-slate-700">{restoreText('restore_finish', rest.restoreFinish)}</span>
                                </div>
                                <div className="flex justify-between gap-2 border-b border-slate-50 py-1">
                                    <span className="text-slate-400">人工标注</span>
                                    <span className="text-slate-700">
                                        {restoreText('restore_label', rest.labelCat)}{rest.labelSub ? ` · ${rest.labelSub}` : ''}
                                    </span>
                                </div>
                                <div className="flex justify-between gap-2 border-b border-slate-50 py-1" title="耗材框占整图面积">
                                    <span className="text-slate-400">框占比</span>
                                    <span className="text-slate-700 font-mono">{rest.restoreAreaPct}%</span>
                                </div>
                                <div className="flex justify-between gap-2 border-b border-slate-50 py-1" title="框外像素漂移 ΔE: 越小说明模型只改了框内, 没动背景">
                                    <span className="text-slate-400">框外漂移 ΔE</span>
                                    <span className="text-slate-700 font-mono">{rest.restoreDrift}</span>
                                </div>
                            </div>
                            {rest.qcAfter && <div className="text-slate-600 bg-slate-50 rounded p-2 leading-relaxed">{rest.qcAfter}</div>}
                            {rest.qcNote && <div className="text-amber-600">质检备注：{rest.qcNote}</div>}
                            <div className="text-[11px] text-slate-400">
                                模型 {rest.restoreModel || '—'} · 质检 {rest.qcBy === 'human' ? '人工改判' : '机器判定'}
                                {rest.batchNo ? ` · 批次 ${rest.batchNo}` : ''}
                            </div>
                            <div className="text-[11px] text-slate-400">
                                质检结论是<b>复核优先级</b>，不是判废依据 —— 「待复核」不代表这张还原图不可用。
                            </div>
                        </div>
                    )}

                    <div className="card p-4 text-xs text-slate-600 space-y-2">
                        <div className="text-xs font-semibold text-slate-500">用户反馈</div>
                        {r.evaluateType === 'none' ? (
                            <div className="text-slate-400">这次加工没有反馈。104,161 条里只有 562 条带反馈，属常态。</div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2"><EvaluateBadge type={r.evaluateType} />{r.evaluateSource && <span className="text-slate-400">来源 {r.evaluateSource}</span>}</div>
                                {opts.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {opts.map((o) => <span key={o} className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-500 text-[11px]">{EVAL_OPTION_LABEL[o] || o}</span>)}
                                    </div>
                                )}
                                {r.evaluateDesc && <div className="text-slate-700 bg-slate-50 rounded p-2 leading-relaxed">{r.evaluateDesc}</div>}
                            </>
                        )}
                    </div>

                    <div className="card p-4 text-xs text-slate-500 space-y-1.5">
                        <div className="text-xs font-semibold text-slate-500 mb-1">溯源</div>
                        <div>源材料 ID: <span className="font-mono">{r.materialIdRaw || '—'}</span></div>
                        <div className="flex items-start gap-2"><span className="shrink-0">平台材料:</span><LinkedMaterial material={data?.material} source={r.materialSource} /></div>
                        <div>用户 UID: <span className="font-mono">{r.userUid || '—'}</span> · 设备 SN: <span className="font-mono">{r.snCode || '—'}</span></div>
                        <div>设备原文: <span className="font-mono">{r.deviceIdRaw}</span>{r.deviceCode ? <> → 平台设备族 <span className="font-mono">{r.deviceCode}</span></> : <span className="text-amber-600"> （未归一）</span>}</div>
                        <div className="text-slate-400 break-all">源文件: <span className="font-mono">{String(extra.zipFilename || r.sourceId)}</span></div>
                    </div>
                </div>

                {/* 右: 元素参数 + processingData + params */}
                <div className="space-y-4">
                    <div className="card p-4">
                        <div className="flex items-baseline gap-2 mb-2">
                            <div className="text-sm font-semibold text-slate-700">元素参数（{data?.elements.length ?? 0}）</div>
                            {laser.length > 0 && <div className="text-[11px] text-slate-400">laser = {JSON.stringify(laser)}（功率百分比，按元素顺序）</div>}
                            {r.elementsTruncated ? <div className="ml-auto text-[11px] text-amber-600">元素超过 200 个，仅存前 200</div> : null}
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                                    <th className="py-1.5 pr-2">#</th>
                                    {ELEMENT_COLS.map(([k, label]) => <th key={k} className="py-1.5 pr-2 whitespace-nowrap">{label}</th>)}
                                </tr></thead>
                                <tbody>
                                    {(data?.elements || []).map((e) => (
                                        <tr key={e.id} className="border-b border-slate-50">
                                            <td className="py-1.5 pr-2 text-slate-400 font-mono">{e.seq + 1}</td>
                                            {ELEMENT_COLS.map(([k]) => {
                                                const v = (e as unknown as Record<string, unknown>)[k];
                                                const empty = v === null || v === undefined || v === '' || v === 0;
                                                const show = empty ? <span className="text-slate-300">—</span> : paramText(k, v);
                                                return <td key={k} className={`py-1.5 pr-2 whitespace-nowrap ${k === 'power' || k === 'speed' ? 'font-mono font-semibold text-slate-800' : 'text-slate-600'}`}>{show}</td>;
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-2">
                            元素级 <b>材料类型</b>取自字段字典的枚举来源（官方材料档 / 用户自定义参数）；官方 {r.officialCount} · 自定义 {r.customCount}。
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="card p-4">
                            <div className="text-sm font-semibold text-slate-700 mb-2">加工通用信息 <span className="text-[11px] text-slate-400 font-normal">processingData</span></div>
                            <KVGrid obj={pd} keyText={kvKeyText} valueText={kvValueText} />
                        </div>
                        <div className="card p-4">
                            <div className="text-sm font-semibold text-slate-700 mb-2">机器参数 <span className="text-[11px] text-slate-400 font-normal">params</span></div>
                            <KVGrid obj={params} keyText={kvKeyText} valueText={kvValueText} />
                        </div>
                    </div>
                </div>
            </div>
            <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />
        </div>
    );
}

export default ProcessHistoryDetail;
